import { Link } from 'react-router-dom';
import { 
  Smartphone, 
  ChefHat, 
  CreditCard, 
  BarChart3,
  Clock,
  Store,
  ArrowRight,
  Check,
  Monitor,
  Tablet,
  Wifi,
  Shield,
  Zap,
  Globe,
  ChevronDown,
  Building2,
  UserCircle,
  ShoppingBag,
  QrCode,
  Receipt,
  TrendingUp,
  Users,
  Bell,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function ProductPage() {
  const products = [
    {
      id: 'pos',
      icon: Smartphone,
      title: 'Cloud POS',
      subtitle: 'Point of Sale',
      description: 'A modern, intuitive point of sale system that works on any device. Take orders, split bills, and manage your floor with ease.',
      features: [
        'Works on iPad, Android, or any browser',
        'Offline mode - never miss a sale',
        'Split payments and tips',
        'Table management and floor plans',
        'Quick service and full service modes',
        'Customer-facing display support'
      ],
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'kds',
      icon: ChefHat,
      title: 'Kitchen Display',
      subtitle: 'KDS System',
      description: 'Keep your kitchen running smoothly with real-time order routing, ticket management, and prep time tracking.',
      features: [
        'Real-time order sync',
        'Multiple station routing',
        'Bump screen functionality',
        'Prep time analytics',
        'Modifier highlighting',
        'Rush order priority'
      ],
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 'kiosk',
      icon: QrCode,
      title: 'Self-Order Kiosk',
      subtitle: 'Customer Ordering',
      description: 'Let customers order directly with an intuitive self-service kiosk. Reduce wait times and increase order accuracy.',
      features: [
        'Touchscreen optimized UI',
        'Upselling prompts',
        'Multiple language support',
        'Loyalty integration',
        'Payment terminal ready',
        'QR code ordering'
      ],
      color: 'from-green-500 to-emerald-600'
    },
    {
      id: 'payments',
      icon: CreditCard,
      title: 'Integrated Payments',
      subtitle: 'Payment Processing',
      description: 'Accept all payment types with competitive rates. One system for cards, mobile pay, gift cards, and more.',
      features: [
        'All major cards accepted',
        'Apple Pay & Google Pay',
        'Tap to pay / NFC',
        'Gift card management',
        'Split tender support',
        'Next-day deposits'
      ],
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'reporting',
      icon: BarChart3,
      title: 'Analytics & Reports',
      subtitle: 'Business Intelligence',
      description: 'Real-time insights to make smarter decisions. Track sales, labor costs, inventory, and customer trends.',
      features: [
        'Real-time sales dashboard',
        'Labor cost tracking',
        'Product mix reports',
        'Hourly/daily/weekly trends',
        'Export to Excel/PDF',
        'Email scheduled reports'
      ],
      color: 'from-cyan-500 to-blue-500'
    },
    {
      id: 'staff',
      icon: Users,
      title: 'Staff Management',
      subtitle: 'Team Tools',
      description: 'Everything you need to manage your team. Scheduling, time tracking, permissions, and payroll integration.',
      features: [
        'Time clock with PIN/photo',
        'Role-based permissions',
        'Shift scheduling',
        'Overtime alerts',
        'Tip pooling',
        'Payroll export'
      ],
      color: 'from-pink-500 to-rose-500'
    },
    {
      id: 'online',
      icon: Globe,
      title: 'Online Ordering',
      subtitle: 'Direct Orders',
      description: 'Your own branded online ordering. Commission-free direct orders that sync straight to your kitchen.',
      features: [
        'Custom branded website',
        'Zero commission fees',
        'Pickup & delivery',
        'Scheduled orders',
        'Menu sync with POS',
        'Customer accounts'
      ],
      color: 'from-indigo-500 to-violet-500'
    },
    {
      id: 'inventory',
      icon: Package,
      title: 'Inventory Management',
      subtitle: 'Stock Control',
      description: 'Track ingredients, get low stock alerts, and manage suppliers. Never run out of what you need.',
      features: [
        'Real-time stock levels',
        'Low stock alerts',
        'Supplier management',
        'Waste tracking',
        'Recipe costing',
        'Purchase orders'
      ],
      color: 'from-amber-500 to-orange-500'
    }
  ];

  const integrations = [
    { name: 'DoorDash', category: 'Delivery' },
    { name: 'UberEats', category: 'Delivery' },
    { name: 'Grubhub', category: 'Delivery' },
    { name: 'QuickBooks', category: 'Accounting' },
    { name: 'Xero', category: 'Accounting' },
    { name: '7shifts', category: 'Scheduling' },
    { name: 'Gusto', category: 'Payroll' },
    { name: 'Mailchimp', category: 'Marketing' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-8 w-8" />
              <span className="text-xl font-bold text-white">Chain29</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/product" className="text-white text-sm font-medium">
                Product
              </Link>
              <Link to="/pricing" className="text-slate-300 hover:text-white text-sm font-medium">
                Pricing
              </Link>
              <Link to="/support" className="text-slate-300 hover:text-white text-sm font-medium">
                Support
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10">
                    Login
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/owner/login" className="flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Restaurant Owner</div>
                        <div className="text-xs text-muted-foreground">Manage your restaurant</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/staff/login" className="flex items-center gap-2 cursor-pointer">
                      <UserCircle className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Employee / Staff</div>
                        <div className="text-xs text-muted-foreground">Clock in & access POS</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="flex items-center gap-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Customer</div>
                        <div className="text-xs text-muted-foreground">Order food online</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/owner/signup">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Everything You Need to<br />
            <span className="text-orange-400">Run Your Restaurant</span>
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            One platform for POS, kitchen display, online ordering, payments, and analytics.
            Built for restaurants of all sizes.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-400">
            <span className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <Monitor className="w-4 h-4" /> Works on any device
            </span>
            <span className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <Wifi className="w-4 h-4" /> Cloud-based
            </span>
            <span className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <Shield className="w-4 h-4" /> Secure & reliable
            </span>
            <span className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <Zap className="w-4 h-4" /> Real-time sync
            </span>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product) => {
              const Icon = product.icon;
              return (
                <Card key={product.id} className="bg-white/5 border-white/10 overflow-hidden hover:border-white/20 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${product.color}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-slate-400 uppercase tracking-wider">{product.subtitle}</p>
                        <h3 className="text-xl font-bold text-white mb-2">{product.title}</h3>
                        <p className="text-slate-300 text-sm mb-4">{product.description}</p>
                        <ul className="grid grid-cols-2 gap-2">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                              <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Works With Your Tools</h2>
          <p className="text-slate-300 mb-8">Connect with popular delivery, accounting, and management apps</p>
          <div className="flex flex-wrap justify-center gap-4">
            {integrations.map((int, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 px-6 py-3 rounded-lg">
                <p className="text-white font-medium">{int.name}</p>
                <p className="text-xs text-slate-400">{int.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Modernize Your Restaurant?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Start your 7-day free trial. Nothing is charged until it ends.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/owner/signup">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-6 w-6" />
              <span className="text-white font-semibold">Chain29</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
              <Link to="/product" className="hover:text-white">Product</Link>
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
            </div>
            <p className="text-sm text-slate-500">© 2026 Chain29. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
